import { forwardRef, useEffect, useRef, useState } from "react";
import { formatDecimal, parseGermanNumber } from "@/lib/formatters";
import { cn } from "@/lib/utils";

type NativeInputProps = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  "value" | "onChange" | "type" | "inputMode"
>;

export interface GermanNumberInputProps extends NativeInputProps {
  value: number | null | undefined;
  onValueChange: (value: number) => void;
  /** Maximale Anzahl der Nachkommastellen, die beim Anzeigen formatiert werden. */
  maxFractionDigits?: number;
  /** Minimale Anzahl der Nachkommastellen für die Anzeige (Standard 0). */
  minFractionDigits?: number;
  /** Untergrenze – Werte unterhalb werden auf min geklemmt. */
  min?: number;
  /** Wenn true, erhält das Feld die shadcn-Input-Basis-Styles. Standard: false. */
  inputStyle?: boolean;
}

function formatForDisplay(
  value: number | null | undefined,
  min: number,
  max: number,
): string {
  if (value == null || !Number.isFinite(value)) return "";
  return formatDecimal(value, max, { minFractionDigits: min });
}

/**
 * Eingabefeld für Zahlen im deutschen Format.
 * - Zeigt Werte mit Tausenderpunkt und Komma als Dezimaltrenner an
 * - Akzeptiert während der Eingabe sowohl Komma als auch Punkt
 * - Liefert intern weiterhin JS-`number` über `onValueChange`
 */
export const GermanNumberInput = forwardRef<HTMLInputElement, GermanNumberInputProps>(
  function GermanNumberInput(
    {
      value,
      onValueChange,
      maxFractionDigits = 2,
      minFractionDigits = 0,
      min,
      inputStyle = false,
      className,
      onFocus,
      onBlur,
      ...rest
    },
    ref,
  ) {
    const baseClass = inputStyle
      ? "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm"
      : "";
    const [text, setText] = useState<string>(() =>
      formatForDisplay(value, minFractionDigits, maxFractionDigits),
    );
    const isFocused = useRef(false);

    useEffect(() => {
      if (!isFocused.current) {
        setText(formatForDisplay(value, minFractionDigits, maxFractionDigits));
      }
    }, [value, minFractionDigits, maxFractionDigits]);

    return (
      <input
        ref={ref}
        {...rest}
        type="text"
        inputMode="decimal"
        className={cn(baseClass, className)}
        value={text}
        onFocus={(e) => {
          isFocused.current = true;
          // Beim Fokussieren ohne Tausenderpunkte zeigen, damit die Eingabe leichter fällt
          if (value != null && Number.isFinite(value)) {
            const raw =
              maxFractionDigits > 0
                ? String(value).replace(".", ",")
                : String(Math.trunc(value));
            setText(raw);
          }
          onFocus?.(e);
        }}
        onChange={(e) => {
          const raw = e.target.value;
          // Nur Ziffern, Komma, Punkt, Minus erlauben
          const cleaned = raw.replace(/[^0-9.,\-]/g, "");
          setText(cleaned);
          // Nur propagieren, wenn die aktuelle Eingabe eine gültige Zahl
          // ergibt – Zwischenstände wie "" oder "1," bleiben unangetastet,
          // damit der Bearbeitungsfluss nicht durch ein erzwungenes 0
          // unterbrochen wird. Endgültige Normalisierung erfolgt onBlur.
          const parsed = parseGermanNumber(cleaned);
          if (parsed !== null) {
            let n = parsed;
            if (typeof min === "number" && n < min) n = min;
            onValueChange(n);
          }
        }}
        onBlur={(e) => {
          isFocused.current = false;
          const parsed = parseGermanNumber(text);
          let n = parsed ?? 0;
          if (typeof min === "number" && n < min) n = min;
          onValueChange(n);
          setText(formatForDisplay(n, minFractionDigits, maxFractionDigits));
          onBlur?.(e);
        }}
      />
    );
  },
);
